'use client'

import { createClient } from './supabase/client'
import { SiteSettings } from '@/types/database'
import { useState, useEffect } from 'react'

// Default site settings - used as fallback if DB settings aren't available
const DEFAULT_SETTINGS: Omit<SiteSettings, 'id' | 'updated_at'> = {
  site_name: 'THCA.SPACE',
  site_logo: '/full-logo.webp',
  dark_mode_logo: '/full-logo-dark.svg', // Default dark mode logo
  primary_color: '#8b5cf6', // Purple
  primary_gradient_from: '#a78bfa', // Lighter Purple
  primary_gradient_to: '#6d28d9', // Darker Purple
  secondary_color: '#3b82f6', // Blue
  secondary_gradient_from: '#60a5fa', // Lighter Blue
  secondary_gradient_to: '#2563eb', // Darker Blue
  accent_color: '#ec4899', // Pink
  background_color: '#020014', // Dark background
  text_color: '#efedfd', // Light text
  
  // Light mode specific surface colors
  light_mode_surface_color: '#f8fafc', // Light surface
  light_mode_surface_hover_color: '#f1f5f9', // Light surface hover
  light_mode_surface_active_color: '#e2e8f0', // Light surface active
  
  company_address: 'Lvovo St. 105A, Vilnius, Lithuania, 08104',
  support_email: 'contact@zeyana.net'
}

/**
 * Hook to get site settings from the client side
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>({
    id: 'loading',
    updated_at: new Date().toISOString(),
    ...DEFAULT_SETTINGS
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    async function fetchSettings() {
      try {
        const supabase = createClient()
        
        // Try to fetch settings from the database
        const { data, error } = await supabase
          .from('site_settings')
          .select('*')
          .order('updated_at', { ascending: false })
          .limit(1)
          .single()
        
        if (error) {
          throw error
        }
        
        if (data) {
          setSettings(data)
        }
      } catch (err) {
        console.error('Error fetching site settings:', err)
        setError(err instanceof Error ? err : new Error('Failed to fetch site settings'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchSettings()
  }, [])

  return { settings, isLoading, error }
}

/**
 * Update theme CSS variables based on site settings
 */
export function applyTheme(settings: SiteSettings, mode: 'light' | 'dark' = 'dark') {
  if (typeof document === 'undefined') return // SSR guard
  
  const root = document.documentElement
  
  // Set theme class
  if (mode === 'dark') {
    root.classList.add('dark')
    root.classList.remove('light')
  } else {
    root.classList.add('light')
    root.classList.remove('dark')
  }
  
  // Set color CSS variables for primary, secondary, accent
  root.style.setProperty('--color-primary', settings.primary_color)
  root.style.setProperty('--color-secondary', settings.secondary_color)
  root.style.setProperty('--color-accent', settings.accent_color)
  
  // Set primary gradient colors with nullish coalescing to handle undefined
  root.style.setProperty('--color-primary-gradient-from', settings.primary_gradient_from ?? settings.primary_color)
  root.style.setProperty('--color-primary-gradient-to', settings.primary_gradient_to ?? settings.primary_color)
  
  // Set secondary gradient colors with nullish coalescing to handle undefined
  root.style.setProperty('--color-secondary-gradient-from', settings.secondary_gradient_from ?? settings.secondary_color)
  root.style.setProperty('--color-secondary-gradient-to', settings.secondary_gradient_to ?? settings.secondary_color)
  
  // Set theme-specific background and text colors 
  if (mode === 'dark') {
    // Dark mode uses the settings directly
    root.style.setProperty('--color-bg-main', settings.background_color)
    root.style.setProperty('--color-text-main', settings.text_color)
  } else {
    // Light mode swaps the colors (dark background becomes light text and vice versa)
    root.style.setProperty('--color-bg-main', settings.text_color)
    root.style.setProperty('--color-text-main', settings.background_color)
  }
  
  // Create lighter and darker variants for each color
  function createVariants(color: string, prefix: string) {
    try {
      // Ensure color is a valid hex code
      if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn(`Invalid color format for ${prefix}: ${color}`);
        // Use fallback colors
        root.style.setProperty(`${prefix}-light`, color); // Default to original
        root.style.setProperty(`${prefix}-dark`, color); // Default to original
        return;
      }
      
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      
      // Lighter variant (blend with white)
      const lightR = Math.min(255, Math.round(r + (255 - r) * 0.3))
      const lightG = Math.min(255, Math.round(g + (255 - g) * 0.3))
      const lightB = Math.min(255, Math.round(b + (255 - b) * 0.3))
      
      // Darker variant (blend with black)
      const darkR = Math.round(r * 0.7)
      const darkG = Math.round(g * 0.7)
      const darkB = Math.round(b * 0.7)
      
      // Set CSS variables
      root.style.setProperty(`${prefix}-light`, `rgb(${lightR}, ${lightG}, ${lightB})`)
      root.style.setProperty(`${prefix}-dark`, `rgb(${darkR}, ${darkG}, ${darkB})`)
    } catch (e) {
      console.error(`Error processing color variants for ${prefix}:`, e);
      // Fallback in case of unexpected errors
      root.style.setProperty(`${prefix}-light`, color);
      root.style.setProperty(`${prefix}-dark`, color);
    }
  }
  
  createVariants(settings.primary_color, '--color-primary')
  createVariants(settings.secondary_color, '--color-secondary')
  createVariants(settings.accent_color, '--color-accent')
} 